import axios from 'axios'
import { useTimeoutFn } from '@vueuse/core'
import { useEprintFunnel } from '@/composables/format-edit/useEprintFunnel'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type {
  EprintActions,
  EprintSession
} from '@/composables/format-edit/useEprintSession'
import type { AxiosLikeError } from '@/utils/format-edit/eprintPdfMessages'

type PaypayStored = {
  printType?: string
  formatId?: string
  fontName?: string
  previewHtml?: string
  merchantPaymentId?: string
  expiryAt?: number
}

export function useEprintPaypayReturn(
  session: EprintSession,
  actions: EprintActions
) {
  const { varuna, message } = useHostServices()
  const funnel = useEprintFunnel()
  let pendingMerchantId = ''

  const recheckTimer = useTimeoutFn(
    () => {
      actions.completePaypayEprint(pendingMerchantId)
    },
    () => session.paypayRecheckDelayMs,
    { immediate: false }
  )

  function getPaypayExpiryAt(): number | null {
    try {
      const raw = localStorage.getItem('paypay_eprint_data')
      if (!raw) return null
      const data = JSON.parse(raw) as PaypayStored
      return data.expiryAt ? Number(data.expiryAt) : null
    } catch (e) {
      console.error('PayPay有効期限の取得に失敗:', e)
      return null
    }
  }

  function getPaypayRemainingSeconds() {
    const expiryAt = getPaypayExpiryAt()
    if (!expiryAt) return 0
    return Math.max(0, Math.floor((expiryAt - Date.now()) / 1000))
  }

  function closePaypayPendingMessage() {
    if (session.paypayPendingMessage) {
      try {
        session.paypayPendingMessage.close()
      } catch (e) {
        console.error('承認待ちメッセージのクローズに失敗:', e)
      }
      session.paypayPendingMessage = null
    }
  }

  function stopPaypayRecheck() {
    recheckTimer.stop()
    session.paypayRecheckCount = 0
    closePaypayPendingMessage()
  }

  function hideDialog() {
    session.visible = false
  }

  function handlePaypayExpired() {
    stopPaypayRecheck()
    session.isPaypayProcessing = false
    funnel.send('paypay_payment_expired', {
      payment_method: 'paypay',
      price: session.eprintPrice,
      print_type: session.printType,
      format_id: session.formatid,
      funnel_session_id: session.funnelSessionId
    })
    message({
      message:
        'PayPayのお支払い有効期限（5分）が切れました。承認が完了していた場合は、コンビニ印刷の番号を登録メールアドレスにお送りしています。届いていない場合は、お手数ですがもう一度お試しください。',
      type: 'warning',
      duration: 0,
      showClose: true
    })
    session.eprintResultCode = '100'
    session.isPrePayment = false
  }

  function handlePaypayPending(merchantPaymentId: string) {
    const expiryAt = getPaypayExpiryAt()
    if (expiryAt && Date.now() >= expiryAt + session.paypayExpiryGraceMs) {
      handlePaypayExpired()
      return
    }
    const remaining = getPaypayRemainingSeconds()
    session.isPaypayProcessing = true
    closePaypayPendingMessage()
    const deadlineText =
      remaining > 0
        ? `お支払いの有効期限まであと約${Math.max(1, Math.ceil(remaining / 60))}分。`
        : ''
    session.paypayPendingMessage = message({
      message: `まだ決済の承認が完了していません。PayPayアプリで承認を完了してください。承認が確認できると自動でコンテンツ番号が表示されます。（${deadlineText}二重のお支払いになるため、この画面でのやり直しはお控えください）`,
      type: 'warning',
      duration: 0,
      showClose: true
    })
    funnel.send('paypay_payment_pending', {
      payment_method: 'paypay',
      remaining_seconds: remaining,
      price: session.eprintPrice,
      print_type: session.printType,
      format_id: session.formatid,
      funnel_session_id: session.funnelSessionId
    })
    if (session.paypayRecheckCount >= session.paypayRecheckMaxCount) {
      closePaypayPendingMessage()
      session.isPaypayProcessing = false
      message({
        message:
          'お支払い状況の確認が完了しませんでした。二重のお支払いを避けるため、やり直す前に登録メールアドレスに番号が届いていないかご確認ください。',
        type: 'warning',
        duration: 0,
        showClose: true
      })
      return
    }
    session.paypayRecheckCount += 1
    pendingMerchantId = merchantPaymentId
    recheckTimer.start()
  }

  function applyPaypaySuccess(responseData: {
    payment_cost: number
    print_type: string
    format_id: string
    merchant_payment_id: string
    payment_history_id?: string
    contents_code: string
    expiration_date: string
  }) {
    session.lastPaymentData = {
      paymentType: 'paypay',
      paymentCost: responseData.payment_cost,
      printType: responseData.print_type,
      formatId: responseData.format_id,
      transactionId: responseData.merchant_payment_id,
      paymentHistoryId: responseData.payment_history_id,
      timestamp: new Date().toISOString()
    }
    session.resultname = 'なし'
    session.eprintResultCode = '200'
    session.eprintContentsCode = responseData.contents_code
    session.expirationDate = responseData.expiration_date
    session.isPrePayment = true
    session.selectedStore = 'lawson'
    funnel.send('paypay_payment_complete', {
      payment_method: 'paypay',
      transaction_id: responseData.merchant_payment_id,
      contents_code: responseData.contents_code,
      expiration_date: responseData.expiration_date,
      price: responseData.payment_cost,
      print_type: responseData.print_type,
      format_id: responseData.format_id,
      funnel_session_id: session.funnelSessionId
    })
    funnel.sendPurchase({
      transaction_id: 'eprint_paypay_' + responseData.merchant_payment_id,
      value: responseData.payment_cost,
      print_type: responseData.print_type,
      product_name: funnel.getPrintTypeLabel(responseData.print_type)
    })
    funnel.send('code_issued', {
      contents_code: responseData.contents_code,
      expiration_date: responseData.expiration_date,
      payment_method: 'prepaid',
      price: responseData.payment_cost,
      print_type: responseData.print_type,
      format_id: responseData.format_id,
      funnel_session_id: session.funnelSessionId
    })
    message({
      message: 'PayPay決済とコンビニ印刷処理が完了しました',
      type: 'success',
      duration: 3000
    })
    if (!session.visible) {
      const savedData = JSON.parse(
        localStorage.getItem('paypay_eprint_data') || '{}'
      ) as PaypayStored
      if (savedData.formatId) {
        actions.openDialog(
          savedData.formatId,
          { innerHTML: savedData.previewHtml || '' },
          savedData.fontName || ''
        )
      } else {
        session.visible = true
      }
    }
  }

  function completePaypayEprint(merchantPaymentId: string) {
    const formData = new FormData()
    formData.append('merchant_payment_id', merchantPaymentId)
    formData.append('version', '2')
    const eprintData = localStorage.getItem('paypay_eprint_data')
    if (eprintData) {
      try {
        const data = JSON.parse(eprintData) as PaypayStored
        if (data.fontName) formData.append('font_name', data.fontName)
        if (data.formatId) formData.append('format_id', data.formatId)
        if (data.printType) formData.append('print_type', data.printType)
        if (data.merchantPaymentId) {
          formData.append('merchant_payment_id_check', data.merchantPaymentId)
        }
      } catch (e) {
        console.error('ローカルストレージのpaypay_eprint_data解析エラー:', e)
      }
    }
    session.isPaypayProcessing = true
    axios
      .post(varuna.endPoints.paypayComplateEprint, formData, { timeout: 60000 })
      .then((response) => {
        if (response.data.status === 'success') {
          stopPaypayRecheck()
          session.isPaypayProcessing = false
          const responseData = response.data.data
          if (responseData && responseData.contents_code) {
            applyPaypaySuccess(responseData)
          } else {
            const errorMsg =
              response.data.message ||
              'PayPay決済は完了しましたが、コンテンツ番号の取得に失敗しました。返金処理を確認中です。'
            funnel.send('paypay_code_issue_failed', {
              payment_method: 'paypay',
              error_message: errorMsg,
              price: session.eprintPrice,
              print_type: session.printType,
              format_id: session.formatid,
              funnel_session_id: session.funnelSessionId
            })
            message({
              message: errorMsg,
              type: 'warning',
              duration: 0,
              showClose: true
            })
            hideDialog()
          }
          localStorage.removeItem('paypay_eprint_data')
          localStorage.removeItem('paypay_eprint_merchant_id')
        } else if (response.data.status === 'unknown') {
          stopPaypayRecheck()
          session.isPaypayProcessing = false
          funnel.send('paypay_payment_unknown', {
            payment_method: 'paypay',
            price: session.eprintPrice,
            print_type: session.printType,
            format_id: session.formatid,
            funnel_session_id: session.funnelSessionId
          })
          message({
            message:
              response.data.message ||
              'お支払い状況を確認できませんでした。二重のお支払いを避けるため、やり直す前に登録メールアドレスに番号が届いていないかご確認ください。',
            type: 'warning',
            duration: 0,
            showClose: true
          })
          hideDialog()
          session.eprintResultCode = '100'
          session.isPrePayment = false
        } else if (response.data.status === 'pending') {
          handlePaypayPending(merchantPaymentId)
        } else {
          stopPaypayRecheck()
          session.isPaypayProcessing = false
          const errorMessage =
            response.data.message || 'PayPay決済が完了していません'
          funnel.send('paypay_payment_failed', {
            payment_method: 'paypay',
            error_status: response.data.status,
            error_message: errorMessage,
            price: session.eprintPrice,
            print_type: session.printType,
            format_id: session.formatid,
            funnel_session_id: session.funnelSessionId
          })
          message({
            message: errorMessage,
            type: 'error',
            duration: 0,
            showClose: true
          })
          hideDialog()
          actions.updatePaymentLogStatus('failed')
          session.eprintResultCode = '100'
          session.isPrePayment = false
        }
      })
      .catch((raw) => {
        const error = raw as AxiosLikeError
        const remainingSeconds = getPaypayRemainingSeconds()
        const isTransport =
          error.code === 'ECONNABORTED' ||
          (error.message || '').includes('timeout') ||
          (!!error.response && error.response.status >= 500)
        if (isTransport && remainingSeconds > 0) {
          handlePaypayPending(merchantPaymentId)
          return
        }
        stopPaypayRecheck()
        session.isPaypayProcessing = false
        let errorMessage = 'PayPay決済の確認に失敗しました'
        if (
          error.code === 'ECONNABORTED' ||
          (error.message || '').includes('timeout')
        ) {
          errorMessage =
            'PayPay決済の確認がタイムアウトしました。しばらく待ってから再度お試しください。'
        } else if (error.response && error.response.status >= 500) {
          errorMessage =
            'サーバーエラーが発生しました。しばらく待ってから再度お試しください。'
        }
        message({
          message: errorMessage,
          type: 'error',
          duration: 0,
          showClose: true
        })
        hideDialog()
        session.eprintResultCode = '100'
        session.isPrePayment = false
      })
  }

  return {
    completePaypayEprint,
    stopPaypayRecheck
  }
}
